/**
 * Adapters between org rubric-scores API payloads and org assessment visualizations.
 */

import {
  findWeightOverride,
  filterEvaluationQuestionSetsForCurrentView,
  filterRubricDocumentForTemplate,
  parseWeight,
  weightedMean,
} from 'js/utils/rubric';

export function isOrgRubricKey(assessmentKey, assessmentHistorySummaries = []) {
  return assessmentHistorySummaries.some(
    (s) => s.assessment_key === assessmentKey && s.assessment_type === 'rubric'
  );
}

/** Team uses document default weights when `scheme_applied` is null/empty. */
export function isNonStandardRubricScheme(schemeApplied) {
  return schemeApplied != null && String(schemeApplied).trim() !== '';
}

/** Human-readable scheme label from API `scheme_applied` (id or legacy name). */
export function rubricSchemeDisplayName(schemeApplied, rubricDocument) {
  if (!isNonStandardRubricScheme(schemeApplied)) {
    return null;
  }

  const override = findWeightOverride(rubricDocument ?? {}, schemeApplied);
  if (!override) {
    return String(schemeApplied).trim();
  }

  const name = String(override.name ?? '').trim();
  return name || String(override.id ?? '').trim();
}

export function rubricTeamsSchemeById(teams, rubricDocument) {
  return Object.fromEntries(
    (teams ?? [])
      .filter((t) => isNonStandardRubricScheme(t.scheme_applied))
      .map((t) => [
        t.team_id,
        rubricSchemeDisplayName(t.scheme_applied, rubricDocument),
      ])
      .filter(([, label]) => label)
  );
}

function aggregateScoreFromFilteredQuestionSets(questionSets) {
  const setPairs = (questionSets ?? [])
    .map((qs) => {
      if (qs.included_in_numeric === false) {
        return null;
      }

      const setWeight = parseWeight(qs.weight);
      if (setWeight == null) {
        return null;
      }

      const questionPairs = (qs.questions ?? [])
        .filter((q) => q.included_in_numeric !== false)
        .map((q) => {
          const score =
            q.score != null && Number.isFinite(Number(q.score))
              ? Number(q.score)
              : null;
          const weight = parseWeight(q.weight);
          return score != null && weight != null ? [score, weight] : null;
        })
        .filter(Boolean);

      const setScore = weightedMean(questionPairs);
      return setScore != null ? [setScore, setWeight] : null;
    })
    .filter(Boolean);

  return weightedMean(setPairs);
}

export function rubricTeamsToTeamScores(teams, document, asOf = new Date()) {
  const date = asOf.toISOString();

  return (teams ?? []).map((team) => {
    const questionSets = filterEvaluationQuestionSetsForCurrentView(
      team.evaluation?.question_sets,
      document,
      asOf
    );
    const score = aggregateScoreFromFilteredQuestionSets(questionSets);

    return {
      team_id: team.team_id,
      team_name: team.team_name,
      scores: score != null ? [{ date, score }] : [],
    };
  });
}

export function rubricTeamsToQuestionScores(
  teams,
  document,
  asOf = new Date()
) {
  return (teams ?? []).map((team) => ({
    team_id: team.team_id,
    team_name: team.team_name,
    assessment_key: team.rubric_id,
    scores: flattenRubricEvaluation(team.evaluation, document, asOf),
  }));
}

function flattenRubricEvaluation(evaluation, document, asOf = new Date()) {
  const questionSets = filterEvaluationQuestionSetsForCurrentView(
    evaluation?.question_sets,
    document,
    asOf
  );

  return questionSets.flatMap((qs) =>
    (qs.questions ?? [])
      .filter((q) => q.score != null && Number.isFinite(Number(q.score)))
      .map((q) => ({
        category: qs.category ?? 'uncategorized',
        question_set_key: qs.key,
        question_key: q.key,
        question_text: q.text ?? q.name ?? q.key,
        question_type: 'likert',
        score: Number(q.score),
      }))
  );
}

export function collectRubricAssessmentKeys(document) {
  const explicit = (document?.assessment_keys ?? []).filter(Boolean);
  const fromSources = [];
  for (const qs of document?.question_sets ?? []) {
    for (const q of qs.questions ?? []) {
      for (const src of q.sources ?? []) {
        if (src?.assessment_key) {
          fromSources.push(src.assessment_key);
        }
      }
    }
  }
  return [...new Set([...explicit, ...fromSources])];
}

export function assessmentNamesFromRubricDocument(document) {
  const names = {};
  for (const qs of document?.question_sets ?? []) {
    for (const q of qs.questions ?? []) {
      for (const src of q.sources ?? []) {
        const key = src?.assessment_key;
        if (!key) continue;
        const name = String(src.assessment_name ?? '').trim();
        if (name && !names[key]) {
          names[key] = name;
        }
      }
    }
  }
  return names;
}

export function filterInheritedBreakouts(breakouts, document) {
  const excluded = new Set(
    (document?.excluded_breakout_ids ?? []).map((id) => String(id))
  );
  return (breakouts ?? []).filter((b) => !excluded.has(String(b.id)));
}

export function inheritedBreakouts(breakouts, document) {
  const keys = new Set(collectRubricAssessmentKeys(document));
  return filterInheritedBreakouts(
    (breakouts ?? []).filter((b) => keys.has(b.assessment_key)),
    document
  );
}

export function rubricDocumentToOrgTemplate(
  rubricId,
  rubricName,
  document,
  asOf = new Date()
) {
  const filtered = filterRubricDocumentForTemplate(document, asOf);
  const questionSets = (filtered?.question_sets ?? []).map((qs) => ({
    key: qs.key,
    name: qs.name ?? qs.key,
    category: qs.category,
    weight: qs.weight,
    questions: (qs.questions ?? []).map((q) => ({
      key: q.key,
      text: q.text ?? q.name ?? q.key,
      elementType: 'question',
      weight: q.weight,
    })),
  }));

  return {
    key: rubricId,
    type: 'rubric',
    name: rubricName,
    views: { orgDetail: ['recommendations', 'squad-scorecard', 'org-trends'] },
    assessment: { question_sets: questionSets },
  };
}
